/**
 * Custom hooks for offline sync and real-time updates
 */
import { useState, useEffect, useCallback } from 'react';
import { offlineSync } from '../services/offlineSync';
import { wsService } from '../services/websocket';

/**
 * Hook for offline sync status and operations
 */
export function useOfflineSync() {
  const [status, setStatus] = useState({
    isOnline: navigator.onLine,
    syncInProgress: false,
    pendingChanges: 0,
    conflicts: 0,
    lastSync: null
  });

  useEffect(() => {
    // Initialize IndexedDB
    offlineSync.init();

    // Update status
    const updateStatus = async () => {
      const newStatus = await offlineSync.getStatus();
      setStatus(newStatus);
    };

    updateStatus();

    // Listen for sync events
    const unsubscribe = offlineSync.addListener((event) => {
      updateStatus();
    });

    return () => unsubscribe();
  }, []);

  const sync = useCallback(() => {
    offlineSync.sync();
  }, []);

  const pullFromServer = useCallback((entityTypes) => {
    return offlineSync.pullFromServer(entityTypes);
  }, []);

  const saveLocal = useCallback((storeName, item, action) => {
    return offlineSync.saveLocal(storeName, item, action);
  }, []);

  const getConflicts = useCallback(() => {
    return offlineSync.getConflicts();
  }, []);

  const resolveConflict = useCallback((conflictId, resolution, mergedData) => {
    return offlineSync.resolveConflict(conflictId, resolution, mergedData);
  }, []);

  return {
    ...status,
    sync,
    pullFromServer,
    saveLocal,
    getConflicts,
    resolveConflict
  };
}

/**
 * Hook for WebSocket real-time updates
 */
export function useWebSocket(options = {}) {
  const { autoConnect = true, rooms = ['dashboard', 'notifications'] } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);

  useEffect(() => {
    if (autoConnect) {
      const token = localStorage.getItem('token');
      if (token) {
        wsService.connect(token, rooms);
      }
    }

    const unsubConnected = wsService.on('connected', () => {
      setIsConnected(true);
    });

    const unsubDisconnected = wsService.on('disconnected', () => {
      setIsConnected(false);
    });

    return () => {
      unsubConnected();
      unsubDisconnected();
    };
  }, [autoConnect, rooms]);

  const subscribe = useCallback((event, callback) => {
    return wsService.on(event, (data) => {
      setLastMessage({ event, data, timestamp: new Date() });
      callback(data);
    });
  }, []);

  const joinRoom = useCallback((room) => {
    wsService.joinRoom(room);
  }, []);

  const leaveRoom = useCallback((room) => {
    wsService.leaveRoom(room);
  }, []);

  const disconnect = useCallback(() => {
    wsService.disconnect();
  }, []);

  return {
    isConnected,
    lastMessage,
    subscribe,
    joinRoom,
    leaveRoom,
    disconnect,
    getStatus: wsService.getStatus.bind(wsService)
  };
}

/**
 * Hook for work order real-time updates
 */
export function useWorkOrderUpdates(onUpdate) {
  const { subscribe } = useWebSocket({ autoConnect: true, rooms: ['work_orders', 'notifications'] });

  useEffect(() => {
    const unsubscribe = subscribe('work_order_update', (data) => {
      if (onUpdate) {
        onUpdate(data);
      }
    });

    return () => unsubscribe();
  }, [subscribe, onUpdate]);
}

/**
 * Hook for dashboard real-time updates
 */
export function useDashboardUpdates(onRefresh) {
  const { subscribe } = useWebSocket({ autoConnect: true, rooms: ['dashboard'] });

  useEffect(() => {
    const unsubscribe = subscribe('dashboard_refresh', (data) => {
      if (onRefresh) {
        onRefresh(data);
      }
    });

    return () => unsubscribe();
  }, [subscribe, onRefresh]);
}

/**
 * Hook for notifications
 */
export function useNotifications(onNotification) {
  const { subscribe } = useWebSocket({ autoConnect: true, rooms: ['notifications'] });
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribe('notification', (data) => {
      setNotifications(prev => [data, ...prev].slice(0, 50)); // Keep last 50
      if (onNotification) {
        onNotification(data);
      }
    });

    return () => unsubscribe();
  }, [subscribe, onNotification]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    clearNotifications
  };
}
